import React, { useState, useMemo, useEffect } from 'react';
import {
  Search,
  MapPin,
  Clock,
  Filter,
  AlertTriangle,
  Image as ImageIcon,
  DollarSign,
  Send,
  CheckCircle2,
  Calendar,
  Eye,
  RefreshCw,
  Sparkles,
  Layers,
  Wrench,
  RotateCcw,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { SERVICE_CATEGORIES } from '../../data/categories';
import { ALL_NIGERIAN_STATES, getLgasForState } from '../../data/nigerianLocations';
import { calculateHaversineDistanceKm, getEstimatedCoordinates } from '../../lib/nigerianLocationResolver';
import { CategoryIcon } from '../../components/common/CategoryIcon';
import { SubmitQuoteModal } from './SubmitQuoteModal';
import { JobDetailsModal } from '../../components/jobs/JobDetailsModal';
import { ShareJobButton } from '../../components/common/ShareJobButton';
import { Job } from '../../types';
import {
  canProfessionalViewAvailableJob,
  getAssignedProfessionalId,
  getJobCanonicalTradeSlug,
  getProfessionalAllowedCategorySlugs,
  tradeToSlug,
} from '../../lib/matchingEngine';

export const AvailableJobsPage: React.FC = () => {
  const { jobs, quotes, currentUser, navigateTo, viewParams } = useApp();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(viewParams?.selectedCategory || 'all');
  const [selectedBudgetRange, setSelectedBudgetRange] = useState('all');
  const [selectedState, setSelectedState] = useState('all');
  const [selectedLga, setSelectedLga] = useState('all');
  const [selectedUrgency, setSelectedUrgency] = useState('all');
  const [activeJobForQuote, setActiveJobForQuote] = useState<Job | null>(null);
  const [activeJobForDetails, setActiveJobForDetails] = useState<Job | null>(null);
  const [handledDeepLinkJobId, setHandledDeepLinkJobId] = useState<string | null>(null);

  // Handle deep-linked jobId from SMS / Email / In-App Job Alert & Quote Invitation
  useEffect(() => {
    const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const targetJobId = viewParams?.jobId || urlParams?.get('jobId') || null;
    const targetAction = (viewParams as any)?.action || urlParams?.get('action') || 'quote';

    if (targetJobId && targetJobId !== handledDeepLinkJobId && jobs.length > 0) {
      const matchedJob = jobs.find((j) => j.id === targetJobId);
      if (matchedJob) {
        setHandledDeepLinkJobId(targetJobId);
        if (targetAction === 'details') {
          setActiveJobForDetails(matchedJob);
        } else {
          setActiveJobForQuote(matchedJob);
        }
      }
    }
  }, [viewParams, jobs, handledDeepLinkJobId]);

  const isProfessionalUser = currentUser?.role === 'professional';
  const allowedProCategorySlugs = useMemo(
    () => (isProfessionalUser ? getProfessionalAllowedCategorySlugs(currentUser) : new Set<string>()),
    [isProfessionalUser, currentUser]
  );

  const selectableCategories = useMemo(() => {
    if (isProfessionalUser && allowedProCategorySlugs.size > 0) {
      return SERVICE_CATEGORIES.filter((cat) => allowedProCategorySlugs.has(cat.slug));
    }
    return SERVICE_CATEGORIES;
  }, [isProfessionalUser, allowedProCategorySlugs]);

  // Available LGAs based on selected state, or dynamically collected LGAs across active jobs
  const availableLgasForSelectedState = useMemo(() => {
    if (selectedState && selectedState !== 'all') {
      return getLgasForState(selectedState);
    }
    // Collect all distinct LGAs present in current eligible open jobs
    const lgaSet = new Set<string>();
    jobs.forEach((j) => {
      if (isProfessionalUser && !canProfessionalViewAvailableJob(j, currentUser)) return;
      if (j.lga && j.lga.trim()) {
        lgaSet.add(j.lga.trim());
      }
    });
    return Array.from(lgaSet).sort();
  }, [selectedState, jobs, isProfessionalUser, currentUser]);

  // Robust multi-criteria job filtering with strict trade & assignment isolation
  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const assignedProId = getAssignedProfessionalId(job);
      const statusLower = String(job.status || '').toLowerCase();
      if (statusLower === 'cancelled' || statusLower === 'expired' || job.cancelled_at || job.expired_at) {
        return false;
      }
      const rawCreated = job.createdAt || (job as any).created_at;
      if (statusLower === 'open' && !assignedProId && rawCreated) {
        const createdMs = new Date(rawCreated).getTime();
        if (!isNaN(createdMs) && Date.now() - createdMs >= 24 * 60 * 60 * 1000) {
          return false;
        }
      }

      // 0. Strict Assignment & Trade Isolation Gate:
      // If a professional is logged in, they MUST ONLY see jobs in their trade or directly assigned to them.
      // An Electrician job must never display to a Plumber, and a job assigned to Artisan A must never display to Artisan B.
      if (isProfessionalUser) {
        if (!canProfessionalViewAvailableJob(job, currentUser)) return false;
      } else {
        // Public/Customer view: only show open, unassigned marketplace jobs
        if (job.status !== 'open' || Boolean(assignedProId)) return false;
      }

      // 1. Trade Category Filter
      if (selectedCategory && selectedCategory !== 'all') {
        const targetCat = tradeToSlug(selectedCategory);
        const canonicalJobSlug = getJobCanonicalTradeSlug(job);
        const jobCatSlug = (job.categorySlug || (job as any).category_slug || '').toLowerCase();
        const matchesCategory =
          canonicalJobSlug === targetCat ||
          jobCatSlug === selectedCategory.toLowerCase();
        if (!matchesCategory) return false;
      }

      // 2. Budget Range Filter
      const budget = Number(job.budgetEstimate ?? (job as any).budget_estimate ?? 0);
      if (selectedBudgetRange && selectedBudgetRange !== 'all') {
        if (selectedBudgetRange === 'under_20k' && budget >= 20000) return false;
        if (selectedBudgetRange === '20k_50k' && (budget < 20000 || budget > 50000)) return false;
        if (selectedBudgetRange === '50k_100k' && (budget < 50000 || budget > 100000)) return false;
        if (selectedBudgetRange === '100k_250k' && (budget < 100000 || budget > 250000)) return false;
        if (selectedBudgetRange === 'above_250k' && budget <= 250000) return false;
      }

      // 3. Location (State & LGA) Filter
      if (selectedState && selectedState !== 'all' && job.state !== selectedState) return false;
      if (selectedLga && selectedLga !== 'all') {
        const targetLga = selectedLga.toLowerCase();
        const jobLga = (job.lga || '').toLowerCase();
        const jobArea = (job.cityArea || '').toLowerCase();
        if (jobLga !== targetLga && !jobArea.includes(targetLga)) return false;
      }

      // 4. Urgency Filter
      if (selectedUrgency && selectedUrgency !== 'all' && job.urgency !== selectedUrgency) return false;

      // 5. Search Query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesSearch =
          job.title.toLowerCase().includes(q) ||
          job.description.toLowerCase().includes(q) ||
          job.lga.toLowerCase().includes(q) ||
          (job.cityArea && job.cityArea.toLowerCase().includes(q)) ||
          job.state.toLowerCase().includes(q) ||
          job.categoryName.toLowerCase().includes(q);
        if (!matchesSearch) return false;
      }

      return true;
    });
  }, [jobs, isProfessionalUser, currentUser, selectedCategory, selectedBudgetRange, selectedState, selectedLga, selectedUrgency, searchQuery]);

  const hasActiveFilters =
    searchQuery.trim() !== '' ||
    selectedCategory !== 'all' ||
    selectedBudgetRange !== 'all' ||
    selectedState !== 'all' ||
    selectedLga !== 'all' ||
    selectedUrgency !== 'all';

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedBudgetRange('all');
    setSelectedState('all');
    setSelectedLga('all');
    setSelectedUrgency('all');
  };

  const getJobDistanceBadge = (job: Job) => {
    if (!currentUser?.state) return null;
    const isSameState = currentUser.state.toLowerCase() === job.state.toLowerCase();
    const isSameLga =
      currentUser.lga && job.lga && currentUser.lga.toLowerCase() === job.lga.toLowerCase();

    let distKm: number | null = null;
    const proCoords =
      currentUser.locationLatitude && currentUser.locationLongitude
        ? { lat: currentUser.locationLatitude, lng: currentUser.locationLongitude }
        : getEstimatedCoordinates(currentUser.state, currentUser.lga, currentUser.cityArea);

    const jobCoords =
      job.locationLatitude && job.locationLongitude
        ? { lat: job.locationLatitude, lng: job.locationLongitude }
        : getEstimatedCoordinates(job.state, job.lga, job.cityArea);

    if (proCoords && jobCoords) {
      distKm = calculateHaversineDistanceKm(
        proCoords.lat,
        proCoords.lng,
        jobCoords.lat,
        jobCoords.lng
      );
    }

    if (distKm !== null && distKm < 80) {
      return (
        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
          📍 {distKm < 1 ? '< 1 km away' : `~${Math.round(distKm)} km away`}
        </span>
      );
    }
    if (isSameLga) {
      return (
        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
          📍 Same LGA ({job.lga})
        </span>
      );
    }
    if (isSameState) {
      return (
        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700">
          📍 Same State ({job.state})
        </span>
      );
    }
    return null;
  };

  return (
    <div className="max-w-7xl mx-auto px-3.5 sm:px-6 lg:px-8 py-4 sm:py-8 space-y-4 sm:space-y-8">
      {/* Header Banner */}
      <div className="bg-slate-900 rounded-2xl sm:rounded-3xl p-5 sm:p-8 text-white relative overflow-hidden shadow-lg">
        <div className="relative z-10 max-w-3xl space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold border border-emerald-500/30">
            <Wrench className="w-3.5 h-3.5" />
            <span>Nigerian Artisan Marketplace • Available Job Board</span>
          </div>
          <h1 className="text-2xl sm:text-4xl font-black tracking-tight">
            Browse Available Service Jobs Across Nigeria
          </h1>
          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
            Filter real customer service requests by trade category, budget range, and location (LGA). Submit direct quotes to win jobs.
          </p>
        </div>
      </div>

      {/* Main Filter Toolbar */}
      <div className="bg-white rounded-3xl p-4 sm:p-6 border border-slate-200/90 shadow-sm space-y-4">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2 text-slate-900 font-black text-sm sm:text-base">
            <Filter className="w-4 h-4 text-emerald-600" />
            <span>Filter Job Requests</span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-600">
              Matches: <strong className="text-emerald-700 font-extrabold">{filteredJobs.length}</strong>
            </span>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs flex items-center gap-1 cursor-pointer transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5 text-slate-500" />
                <span>Reset Filters</span>
              </button>
            )}
          </div>
        </div>

        {/* Filters Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
          {/* 1. Keyword Search */}
          <div className="relative">
            <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">
              Keyword or Fault
            </label>
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
              <input
                type="text"
                placeholder="Search plumbing, wiring, Ikeja..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-hidden focus:border-emerald-500 font-medium"
              />
            </div>
          </div>

          {/* 2. Trade Category Filter */}
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">
              {isProfessionalUser ? 'Your Qualified Trade(s)' : 'Trade Category'}
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-xs text-slate-800 font-bold bg-white focus:outline-hidden focus:border-emerald-500"
            >
              <option value="all">
                {isProfessionalUser
                  ? `My Registered Trades (${selectableCategories.length})`
                  : `All Service Trades (${selectableCategories.length})`}
              </option>
              {selectableCategories.map((cat) => (
                <option key={cat.slug} value={cat.slug}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* 3. Budget Range Filter */}
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">
              Target Budget Range
            </label>
            <select
              value={selectedBudgetRange}
              onChange={(e) => setSelectedBudgetRange(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-xs text-slate-800 font-bold bg-white focus:outline-hidden focus:border-emerald-500"
            >
              <option value="all">All Budget Ranges</option>
              <option value="under_20k">Under ₦20,000</option>
              <option value="20k_50k">₦20,000 – ₦50,000</option>
              <option value="50k_100k">₦50,000 – ₦100,000</option>
              <option value="100k_250k">₦100,000 – ₦250,000</option>
              <option value="above_250k">Above ₦250,000</option>
            </select>
          </div>

          {/* 4. Location LGA Filter */}
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">
              Location (LGA)
            </label>
            <select
              value={selectedLga}
              onChange={(e) => setSelectedLga(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-xs text-slate-800 font-bold bg-white focus:outline-hidden focus:border-emerald-500"
            >
              <option value="all">All Local Government Areas (LGAs)</option>
              {availableLgasForSelectedState.map((lga) => (
                <option key={lga} value={lga}>
                  {lga} {selectedState && selectedState !== 'all' ? `(${selectedState})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Secondary Row: State & Urgency */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs pt-2 border-t border-slate-100">
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">
              Filter by State
            </label>
            <select
              value={selectedState}
              onChange={(e) => {
                setSelectedState(e.target.value);
                setSelectedLga('all');
              }}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs text-slate-700 bg-white"
            >
              <option value="all">All 36 States + FCT</option>
              {ALL_NIGERIAN_STATES.map((s) => (
                <option key={s.name} value={s.name}>
                  {s.name} {s.capital ? `(${s.capital})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">
              Filter by Urgency Level
            </label>
            <select
              value={selectedUrgency}
              onChange={(e) => setSelectedUrgency(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs text-slate-700 bg-white"
            >
              <option value="all">Any Timing / Urgency</option>
              <option value="emergency">Emergency Repair (Same Day)</option>
              <option value="within_48h">Within 48 Hours</option>
              <option value="flexible">Flexible Timing</option>
            </select>
          </div>
        </div>
      </div>

      {/* Jobs Grid */}
      <div className="space-y-3 sm:space-y-4">
        {filteredJobs.length === 0 ? (
          <div className="bg-white rounded-3xl p-8 sm:p-12 text-center border border-slate-200/90 shadow-2xs space-y-3">
            <Clock className="w-10 h-10 text-slate-300 mx-auto" />
            <h3 className="text-base font-black text-slate-900">No jobs match your current filter criteria</h3>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              Try adjusting your trade category, budget range, or location filters to discover open customer requests across Nigeria.
            </p>
            <button
              onClick={clearAllFilters}
              className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-extrabold cursor-pointer transition-colors shadow-xs"
            >
              Reset All Filters
            </button>
          </div>
        ) : (
          filteredJobs.map((job) => {
            const myQuoteForThis = currentUser
              ? quotes.find((q) => q.jobId === job.id && q.professionalId === currentUser.id)
              : undefined;

            return (
              <div
                key={job.id}
                id={`available-job-card-${job.id}`}
                className="bg-white rounded-3xl p-5 sm:p-6 border border-slate-200/90 hover:border-emerald-500/50 shadow-2xs hover:shadow-md transition-all flex flex-col lg:flex-row lg:items-center justify-between gap-4 sm:gap-6"
              >
                <div className="space-y-2 sm:space-y-3 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                    <span className="px-2.5 py-0.5 sm:py-1 rounded-lg text-[11px] sm:text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 flex items-center gap-1.5">
                      <CategoryIcon slug={job.categorySlug} className="w-3.5 h-3.5" />
                      {job.categoryName}
                    </span>

                    {job.urgency === 'emergency' ? (
                      <span className="px-2 py-0.5 sm:py-1 rounded-lg text-[10px] sm:text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200">
                        🚨 Emergency Repair
                      </span>
                    ) : job.urgency === 'within_48h' ? (
                      <span className="px-2 py-0.5 sm:py-1 rounded-lg text-[10px] sm:text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                        ⚡ Within 48 Hours
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 sm:py-1 rounded-lg text-[10px] sm:text-xs font-medium bg-slate-100 text-slate-600">
                        🗓️ Flexible
                      </span>
                    )}

                    <span className="text-[11px] sm:text-xs text-slate-400">
                      Posted {new Date(job.createdAt).toLocaleDateString()}
                    </span>
                  </div>

                  <h3 className="text-base sm:text-lg font-black text-slate-900">{job.title}</h3>

                  <p className="text-xs text-slate-600 leading-relaxed line-clamp-2 max-w-3xl">
                    {job.description}
                  </p>

                  <div className="flex flex-wrap items-center gap-2.5 sm:gap-4 text-xs text-slate-500 pt-1">
                    <span className="flex items-center gap-1.5 font-bold text-slate-800">
                      <MapPin className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span className="truncate">{job.cityArea ? `${job.cityArea}, ` : ''}{job.lga}, {job.state}</span>
                    </span>

                    {getJobDistanceBadge(job)}

                    {job.photos && job.photos.length > 0 && (
                      <span className="flex items-center gap-1 text-slate-600">
                        <ImageIcon className="w-4 h-4 text-slate-400 shrink-0" />
                        {job.photos.length} Photo{job.photos.length > 1 ? 's' : ''}
                      </span>
                    )}

                    <span className="text-slate-500">
                      Customer: <strong>{job.customerName}</strong>
                    </span>

                    <span className="text-emerald-700 font-bold">
                      {job.quotesCount || 0} Quotes Received
                    </span>
                  </div>
                </div>

                {/* Right Action Block */}
                <div className="shrink-0 flex flex-col sm:flex-row lg:flex-col items-stretch sm:items-center lg:items-end justify-between gap-3 sm:gap-4 border-t lg:border-t-0 pt-3 lg:pt-0">
                  <div className="flex items-center justify-between sm:block lg:text-right">
                    <span className="text-[10px] uppercase font-bold text-slate-400 block">Target Budget</span>
                    <span className="text-base sm:text-2xl font-black text-slate-900">
                      ₦{(job.budgetEstimate || 0).toLocaleString()}
                    </span>
                  </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <ShareJobButton job={job} variant="icon" showDropdown={true} />
                      <button
                        type="button"
                        onClick={() => setActiveJobForDetails(job)}
                        className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs flex items-center justify-center gap-1.5 transition-colors min-h-[44px] cursor-pointer"
                      >
                        <Eye className="w-4 h-4" />
                        <span>Details & Media</span>
                      </button>

                    {myQuoteForThis ? (
                      <span className="inline-flex items-center justify-center gap-1 text-xs font-extrabold text-emerald-800 bg-emerald-50 px-4 py-2.5 rounded-xl border border-emerald-200 min-h-[44px]">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        Quote Sent (₦{myQuoteForThis.totalAmount.toLocaleString()})
                      </span>
                    ) : currentUser?.role === 'professional' ? (
                      <button
                        id={`quote-action-btn-${job.id}`}
                        onClick={() => setActiveJobForQuote(job)}
                        className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs shadow-md transition-all flex items-center justify-center gap-2 min-h-[44px] cursor-pointer"
                      >
                        <Send className="w-4 h-4" />
                        <span>Submit Quote</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => navigateTo('pro_auth', { authMode: 'login' })}
                        className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow-xs min-h-[44px] flex items-center justify-center cursor-pointer"
                      >
                        Sign In as Pro to Quote
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Full Job Details & Media Modal */}
      {activeJobForDetails && (
        <JobDetailsModal
          job={activeJobForDetails}
          isOpen={Boolean(activeJobForDetails)}
          onClose={() => setActiveJobForDetails(null)}
          onOpenSubmitQuote={(jobToQuote) => setActiveJobForQuote(jobToQuote)}
        />
      )}

      {/* Quote Submission Modal */}
      {activeJobForQuote && (
        <SubmitQuoteModal
          job={activeJobForQuote}
          isOpen={Boolean(activeJobForQuote)}
          onClose={() => setActiveJobForQuote(null)}
        />
      )}
    </div>
  );
};
